import type { MusinsaStandardResponse } from '@musinsa/packages-http';
import { executeStandardRequest, queryStringify } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import { z } from 'zod';
import { privateHttpClient } from '../httpClient';
import { productsApiPath } from './apiPath';

export const SearchProductsRequestSchema = z.object({
  keyword: z.string().min(1),
  page: z.number().min(1),
  size: z.number().min(1).max(100),
});

export type SearchProductsRequest = z.infer<typeof SearchProductsRequestSchema>;

const SearchedProductSchema = z.object({
  goodsNo: z.number(),
  goodsName: z.string(),
  brandNo: z.number().optional(),
  brandName: z.string(),
  imageUrl: z.string(),
  webUrl: z.string(),
  originalPrice: z.number(),
  price: z.number(),
  salesRate: z.number().nullish(),
  couponPrice: z.number().nullish(),
  couponSalesRate: z.number().nullish(),
  hasOptionPrice: z.boolean().nullish(),
  extraDiscountAmount: z
    .number()
    .int()
    .nullish()
    .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
  finalPrice: z
    .number()
    .int()
    .nullish()
    .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
  finalDiscount: z
    .number()
    .int()
    .nullish()
    .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
  isLowestPrice: z
    .boolean()
    .nullish()
    .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
  isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
  existsCoupon: z.boolean().nullish().describe('쿠폰 존재 여부'),
  heartCount: z.number(),
  reviewCount: z.number(),
  reviewScore: z.string(),
  isLiked: z.boolean(),
  expectedEarnings: z.number(),
  largeCategoryNo: z.number().optional().nullable(),
  largeCategoryName: z.string().optional().nullable(),
  middleCategoryNo: z.number().optional().nullable(),
  middleCategoryName: z.string().optional().nullable(),
  smallCategoryNo: z.number().optional().nullable(),
  smallCategoryName: z.string().optional().nullable(),
});

export const SearchProductsResponseDataSchema = z.object({
  list: z.array(SearchedProductSchema),
});

export type SearchProductsResponseData = z.infer<typeof SearchProductsResponseDataSchema>;

export type SearchProductsResponse = MusinsaStandardResponse<SearchProductsResponseData>;

export const getSearchProducts = (params: SearchProductsRequest) =>
  executeStandardRequest(() =>
    privateHttpClient.get<SearchProductsResponse>(
      urlJoin(productsApiPath.searchProducts),
      {
        ...params,
      },
      {
        paramsSerializer: (params) => queryStringify(params, { arrayFormat: 'comma' }),
      },
    ),
  );
